import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './CourseWizard.css';
import Toast from './Toast.jsx';
import Loader from './Loader.jsx';

const CourseWizard = ({ onClose, onCourseCreated, programs = [], editMode = false, courseId = null, facultyId: propFacultyId = null }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(editMode);
  const [toast, setToast] = useState(null);
  const [currentCourseId, setCurrentCourseId] = useState(courseId);
  const [facultyId, setFacultyId] = useState(propFacultyId || localStorage.getItem('fac_id'));
  const [academicYears, setAcademicYears] = useState([]);
  const [existingCodes, setExistingCodes] = useState([]);
  const [codeError, setCodeError] = useState('');
  const [codeChecking, setCodeChecking] = useState(false);
  const [showExistingCodes, setShowExistingCodes] = useState(false);

  // Fetch academic years, existing course codes, and resolve facultyId on mount
  useEffect(() => {
    const fetchAcademicYears = async () => {
      try {
        const res = await axios.get('/api/academic-years');
        setAcademicYears(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Failed to fetch academic years:', err);
      }
    };
    const fetchExistingCodes = async () => {
      try {
        const res = await axios.get('/api/course-codes');
        setExistingCodes(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Failed to fetch course codes:', err);
      }
    };
    const resolveFacultyId = async () => {
      if (facultyId) return; // already have it
      try {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = userData.user_id;
        if (userId) {
          const res = await axios.get(`/api/faculty/by-user/${userId}`);
          if (res.data?.fac_id) {
            setFacultyId(res.data.fac_id);
            console.log('[CourseWizard] Resolved fac_id from user_id:', res.data.fac_id);
          }
        }
      } catch (err) {
        console.error('Failed to resolve faculty ID:', err);
      }
    };
    fetchAcademicYears();
    fetchExistingCodes();
    resolveFacultyId();
  }, []);

  // Step 1: Course Details
  const [courseData, setCourseData] = useState({
    course_title: '',
    course_code: '',
    description: '',
    program_id: '',
    acd_id: '',
    duration_weeks: 12,
    publish_status: 'active',
    thumbnail: null
  });

  // Validate course code when it changes (debounced)
  useEffect(() => {
    const code = courseData.course_code.trim();
    if (!code) {
      setCodeError('');
      return;
    }
    // Skip validation in edit mode for the current course's own code
    if (editMode) {
      setCodeError('');
      return;
    }
    const timer = setTimeout(async () => {
      // First check locally
      if (existingCodes.map(c => c.toUpperCase()).includes(code.toUpperCase())) {
        setCodeError(`Course code "${code}" already exists. Please use a different code.`);
        return;
      }
      // Then verify with backend
      setCodeChecking(true);
      try {
        const res = await axios.get(`/api/course-code-check/${code}`);
        if (res.data.exists) {
          setCodeError(`Course code "${code}" already exists. Please use a different code.`);
        } else {
          setCodeError('');
        }
      } catch {
        // If API fails, rely on local check only
        setCodeError('');
      } finally {
        setCodeChecking(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [courseData.course_code, existingCodes, editMode]);

  // Step 2: Syllabus Units (title + description only)
  const [units, setUnits] = useState([]);

  const addUnit = () => {
    setUnits(prev => [...prev, { unit_id: null, title: '', description: '', unit_order: prev.length + 1 }]);
  };

  const updateUnit = (index, field, value) => {
    setUnits(prev => prev.map((u, i) => (i === index ? { ...u, [field]: value } : u)));
  };

  const removeUnit = (index) => {
    setUnits(prev => prev.filter((_, i) => i !== index).map((u, i) => ({ ...u, unit_order: i + 1 })));
  };

  // Step 3: Weekly Plan
  const [weeks, setWeeks] = useState(
    Array.from({ length: 12 }, (_, i) => ({
      week_number: i + 1,
      title: `Week ${i + 1}`,
      description: '',
      unit_ids: []
    }))
  );

  // Step 4: Weekly Content
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [weekContent, setWeekContent] = useState({
    materials: [{ type: 'video', title: '', url: '', file: null, description: '' }],
    assignment: null,
    test: null
  });
  const [weekContentMap, setWeekContentMap] = useState({});

  // Save current week & switch to another week in Step 4
  const selectWeekForContent = (weekNum) => {
    const updatedMap = { ...weekContentMap, [selectedWeek]: { ...weekContent } };
    setWeekContentMap(updatedMap);
    const stored = updatedMap[weekNum];
    if (stored) {
      setWeekContent({ ...stored });
    } else {
      setWeekContent({
        materials: [{ type: 'video', title: '', url: '', file: null, description: '' }],
        assignment: null,
        test: null
      });
    }
    setSelectedWeek(weekNum);
  };

  // Load existing course data when in edit mode
  useEffect(() => {
    if (editMode && currentCourseId) {
      loadCourseData();
    }
  }, [editMode, currentCourseId]);

  const loadCourseData = async () => {
    try {
      setLoading(true);
      const [courseRes, unitsRes, weeksRes, contentsRes, assignmentsRes, testsRes] = await Promise.all([
        axios.get(`/api/courses/${currentCourseId}`),
        axios.get(`/api/syllabus-units/${currentCourseId}`).catch(() => ({ data: [] })),
        axios.get(`/api/weekly-plan/${currentCourseId}`).catch(() => ({ data: [] })),
        axios.get(`/api/contents/by-course/${currentCourseId}`).catch(() => ({ data: [] })),
        axios.get(`/api/assignments/by-course/${currentCourseId}`).catch(() => ({ data: [] })),
        axios.get(`/api/tests/by-course/${currentCourseId}/full`).catch(() => ({ data: [] }))
      ]);
      const course = courseRes.data;

      // Step 1: Course details
      setCourseData({
        course_title: course.course_name || '',
        course_code: course.course_code || '',
        description: course.description || '',
        program_id: course.prg_id || '',
        acd_id: course.acd_id || '',
        duration_weeks: course.duration_weeks || 12,
        publish_status: course.status?.toLowerCase() || 'active',
        thumbnail: null
      });

      // Step 2: Load existing syllabus units
      const loadedUnits = (unitsRes.data || []).map((u, i) => ({
        unit_id: u.unit_id,
        title: u.title || '',
        description: u.description || '',
        unit_order: u.unit_order || i + 1
      }));
      if (loadedUnits.length > 0) setUnits(loadedUnits);

      // Step 3: Load weekly plan with unit mappings
      const numWeeks = course.duration_weeks || 12;
      const loadedWeeks = weeksRes.data || [];
      if (loadedWeeks.length > 0) {
        const weeksList = loadedWeeks.map(w => ({
          week_id: w.week_id,
          week_number: w.week_number,
          title: w.title || `Week ${w.week_number}`,
          description: w.description || '',
          unit_ids: w.unit_ids || []
        }));
        for (let i = weeksList.length; i < numWeeks; i++) {
          weeksList.push({ week_number: i + 1, title: `Week ${i + 1}`, description: '', unit_ids: [] });
        }
        setWeeks(weeksList.slice(0, numWeeks));
      } else {
        setWeeks(Array.from({ length: numWeeks }, (_, i) => ({
          week_number: i + 1, title: `Week ${i + 1}`, description: '', unit_ids: []
        })));
      }

      // Step 4: Build weekContentMap from content, assignments, tests
      const contentByWeek = {};
      for (let i = 1; i <= numWeeks; i++) {
        contentByWeek[i] = { materials: [], assignment: null, test: null };
      }
      (contentsRes.data || []).forEach(c => {
        const wn = c.week_no || 0;
        if (wn > 0 && contentByWeek[wn]) {
          contentByWeek[wn].materials.push({
            content_id: c.content_id, type: c.content_type || 'video',
            title: c.title || '', url: c.file_path || '', description: ''
          });
        }
      });
      (assignmentsRes.data || []).forEach(a => {
        const wn = a.week_no || 0;
        if (wn > 0 && contentByWeek[wn] && !contentByWeek[wn].assignment) {
          contentByWeek[wn].assignment = {
            assignment_id: a.assignment_id, title: a.title || '',
            description: a.description || '', due_date: a.due_date || ''
          };
        }
      });
      (testsRes.data || []).forEach(t => {
        const wn = t.week_no || 0;
        if (wn > 0 && contentByWeek[wn] && !contentByWeek[wn].test) {
          // Parse questions into editable format - handle all DB formats
          let rawQuestions = t.questions;
          // Handle wrapped format: {"questions": [...]}
          if (rawQuestions && typeof rawQuestions === 'object' && !Array.isArray(rawQuestions)) {
            if (Array.isArray(rawQuestions.questions)) {
              rawQuestions = rawQuestions.questions;
            } else {
              rawQuestions = Object.values(rawQuestions);
            }
          }
          // Handle double-encoded JSON string
          if (typeof rawQuestions === 'string') {
            try { rawQuestions = JSON.parse(rawQuestions); } catch(e) { rawQuestions = []; }
          }
          if (!Array.isArray(rawQuestions)) rawQuestions = [];

          const mcqQuestions = rawQuestions
            .filter(q => typeof q === 'object' && q !== null)
            .map((q, idx) => {
              // Question text: try all known keys
              const qText = q.question_text || q.question || q.text || q.prompt || '';
              // Options: handle dict {a,b,c,d}, array [...], or flat option_a/b/c/d
              const opts = q.options;
              let optA = '', optB = '', optC = '', optD = '';
              if (opts && typeof opts === 'object' && !Array.isArray(opts)) {
                optA = opts.a || opts.A || '';
                optB = opts.b || opts.B || '';
                optC = opts.c || opts.C || '';
                optD = opts.d || opts.D || '';
              } else if (Array.isArray(opts)) {
                optA = opts[0] || '';
                optB = opts[1] || '';
                optC = opts[2] || '';
                optD = opts[3] || '';
              }
              // Fallback to flat keys
              if (!optA) optA = q.option_a || '';
              if (!optB) optB = q.option_b || '';
              if (!optC) optC = q.option_c || '';
              if (!optD) optD = q.option_d || '';

              return {
                question_text: qText,
                option_a: optA,
                option_b: optB,
                option_c: optC,
                option_d: optD,
                correct_option: q.correct_option || q.correct_answer || q.answer || 'a',
                marks: q.marks || 1
              };
            });
          contentByWeek[wn].test = {
            test_id: t.test_id, title: t.title || '',
            duration_minutes: t.duration_minutes || 30,
            questions: mcqQuestions
          };
        }
      });
      for (let i = 1; i <= numWeeks; i++) {
        if (contentByWeek[i].materials.length === 0) {
          contentByWeek[i].materials = [{ type: 'video', title: '', url: '', file: null, description: '' }];
        }
      }
      setWeekContentMap(contentByWeek);
      setWeekContent(contentByWeek[1] || { materials: [{ type: 'video', title: '', url: '', file: null, description: '' }], assignment: null, test: null });

      setToast({ type: 'success', message: 'Course data loaded successfully' });
    } catch (error) {
      setToast({ type: 'error', message: 'Failed to load course data' });
      console.error('Error loading course:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle Step 1: Course Creation or Update
  const handleStep1Submit = async () => {
    if (!courseData.course_title || !courseData.program_id || !courseData.description || !courseData.course_code) {
      setToast({ type: 'error', message: 'Please fill all required fields (Title, Code, Description, Program)' });
      return;
    }
    if (codeError) {
      setToast({ type: 'error', message: codeError });
      return;
    }
    if (!editMode && !facultyId) {
      setToast({ type: 'error', message: 'Faculty ID could not be resolved. Please reload the page.' });
      return;
    }

    setLoading(true);
    try {
      if (editMode && currentCourseId) {
        // UPDATE existing course
        await axios.put(`/api/courses/${currentCourseId}`, {
          course_name: courseData.course_title,
          description: courseData.description,
          prg_id: courseData.program_id,
          duration_weeks: parseInt(courseData.duration_weeks),
          status: courseData.publish_status
        });
        setToast({ type: 'success', message: 'Course updated successfully!' });
      } else {
        // CREATE new course
        const payload = {
          course_name: courseData.course_title,
          course_code: courseData.course_code,
          description: courseData.description,
          prg_id: courseData.program_id,
          fac_id: facultyId,
          acd_id: courseData.acd_id || undefined,
          duration_weeks: parseInt(courseData.duration_weeks),
          status: courseData.publish_status
        };
        const response = await axios.post('/api/courses', payload);
        setCurrentCourseId(response.data.course_id);

        // Upload thumbnail if selected
        if (courseData.thumbnail) {
          const formData = new FormData();
          formData.append('course_image', courseData.thumbnail);
          await axios.put(`/api/courses/${response.data.course_id}/image`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }

        setToast({ type: 'success', message: 'Course created successfully!' });
      }

      // Upload thumbnail on edit if a new file was selected
      if (editMode && currentCourseId && courseData.thumbnail) {
        const formData = new FormData();
        formData.append('course_image', courseData.thumbnail);
        await axios.put(`/api/courses/${currentCourseId}/image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      // Regenerate weeks if changed
      const numWeeks = parseInt(courseData.duration_weeks) || 12;
      setWeeks(prev => {
        if (prev.length === numWeeks) return prev;
        return Array.from({ length: numWeeks }, (_, i) => ({
          week_number: i + 1,
          title: prev[i]?.title || `Week ${i + 1}`,
          description: prev[i]?.description || '',
          unit_ids: prev[i]?.unit_ids || []
        }));
      });

      setStep(2);
    } catch (error) {
      console.error('Step 1 error:', error);
      setToast({ type: 'error', message: error.response?.data?.error || error.response?.data?.message || 'Failed to save course' });
    } finally {
      setLoading(false);
    }
  };

  // Handle Step 2: Save units via syllabus-units API
  const handleStep2Submit = async () => {
    const validUnits = units.filter(u => u.title && u.title.trim());
    if (validUnits.length === 0) {
      setToast({ type: 'error', message: 'Add at least one unit with a title' });
      return;
    }

    setLoading(true);
    try {
      for (let i = 0; i < validUnits.length; i++) {
        const unit = validUnits[i];
        if (unit.unit_id) {
          await axios.put(`/api/syllabus-units/${unit.unit_id}`, {
            title: unit.title.trim(),
            description: (unit.description || '').trim(),
            unit_order: i + 1
          });
        } else {
          const res = await axios.post('/api/syllabus-units', {
            course_id: currentCourseId,
            title: unit.title.trim(),
            description: (unit.description || '').trim(),
            unit_order: i + 1
          });
          validUnits[i].unit_id = res.data.unit_id;
        }
      }
      setUnits(validUnits.map((u, i) => ({ ...u, unit_order: i + 1 })));
      setToast({ type: 'success', message: 'Units saved successfully!' });
      setStep(3);
    } catch (error) {
      setToast({ type: 'error', message: error.response?.data?.message || 'Failed to save units' });
    } finally {
      setLoading(false);
    }
  };

  // Handle Step 3: Weekly Plan with unit mapping — use weekly-plan API
  const handleStep3Submit = async () => {
    setLoading(true);
    try {
      const updatedWeeks = [...weeks];
      const MAX_RETRIES = 3;

      for (let i = 0; i < updatedWeeks.length; i++) {
        const w = updatedWeeks[i];
        let lastError = null;

        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
          try {
            if (w.week_id) {
              // Update existing week
              await axios.put(`/api/weekly-plan/${w.week_id}`, {
                title: w.title,
                description: w.description || '',
                unit_ids: w.unit_ids || [],
                week_number: w.week_number
              });
            } else {
              // Create new week
              const res = await axios.post('/api/weekly-plan', {
                course_id: currentCourseId,
                week_number: w.week_number,
                title: w.title,
                description: w.description || '',
                unit_ids: w.unit_ids || [],
                week_order: w.week_number
              });
              updatedWeeks[i] = { ...w, week_id: res.data.week_id };
            }
            lastError = null;
            break; // Success, exit retry loop
          } catch (error) {
            lastError = error;
            if (attempt < MAX_RETRIES) {
              // Wait 500ms before retrying
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }
        }

        if (lastError) {
          throw new Error(`Failed to save Week ${w.week_number} after ${MAX_RETRIES} attempts: ${lastError.message}`);
        }
      }

      setWeeks(updatedWeeks);
      setToast({ type: 'success', message: 'Weekly plan saved successfully!' });
      setStep(4);
    } catch (error) {
      console.error('Step 3 save error:', error);
      setToast({ type: 'error', message: error.response?.data?.error || error.message || 'Failed to save weekly plan' });
    } finally {
      setLoading(false);
    }
  };

  // Toggle a unit assignment for a week
  const toggleUnitForWeek = (weekIdx, unitId) => {
    setWeeks(prev => prev.map((w, i) => {
      if (i !== weekIdx) return w;
      const ids = w.unit_ids || [];
      const exists = ids.includes(unitId);
      return { ...w, unit_ids: exists ? ids.filter(id => id !== unitId) : [...ids, unitId] };
    }));
  };

  // Handle finishing the course from any week in step 4
  const handleFinishCourse = async () => {
    await handleStep4Submit(true);
  };

  // Handle Step 4: Weekly Content — save new + update existing
  const handleStep4Submit = async (finishAfterSave = false) => {
    setLoading(true);
    try {
      const currentWeek = weeks.find(w => w.week_number === selectedWeek);
      const weekId = currentWeek?.week_id;

      // Save/update materials
      for (const material of weekContent.materials) {
        if (!material.title) continue; // skip empty rows
        if (material.content_id) {
          // Update existing content
          if (material.file) {
            // Replace file via multipart PUT
            const formData = new FormData();
            formData.append('file', material.file);
            formData.append('title', material.title);
            formData.append('content_type', material.type);
            formData.append('week_no', selectedWeek);
            await axios.put(`/api/contents/${material.content_id}`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } else {
            await axios.put(`/api/contents/${material.content_id}`, {
              title: material.title,
              content_type: material.type,
              week_no: selectedWeek
            });
          }
        } else if (material.file) {
          // Create new content with file upload
          const formData = new FormData();
          formData.append('file', material.file);
          formData.append('course_id', currentCourseId);
          formData.append('faculty_id', facultyId);
          formData.append('title', material.title);
          formData.append('content_type', material.type);
          formData.append('week_no', selectedWeek);
          const res = await axios.post('/api/contents', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          material.content_id = res.data.content_id;
        }
      }

      // Save/update assignment
      if (weekContent.assignment?.title) {
        if (weekContent.assignment.assignment_id) {
          await axios.put(`/api/assignments/${weekContent.assignment.assignment_id}`, {
            title: weekContent.assignment.title,
            description: weekContent.assignment.description || '',
            due_date: weekContent.assignment.due_date || null
          });
        } else if (weekId) {
          await axios.post('/api/assignments/create-with-week', {
            course_id: currentCourseId,
            week_id: weekId,
            week_number: selectedWeek,
            title: weekContent.assignment.title,
            description: weekContent.assignment.description || '',
            due_date: weekContent.assignment.due_date || null
          });
        }
      }

      // Save or update test
      if (weekContent.test?.title) {
        const mcqPayload = (weekContent.test.questions || []).map((q, idx) => ({
          question_text: q.question_text || '',
          options: {
            a: q.option_a || '',
            b: q.option_b || '',
            c: q.option_c || '',
            d: q.option_d || ''
          },
          correct_option: q.correct_option || 'a',
          marks: q.marks || 1
        }));

        // Safety guard: don't overwrite existing test with all-empty questions
        const hasAnyContent = mcqPayload.some(q => q.question_text.trim() || q.options.a.trim() || q.options.b.trim());

        if (weekContent.test.test_id) {
          // Update existing test — only send questions if they have content
          const updatePayload = {
            title: weekContent.test.title,
            type: 'mcq',
            duration_minutes: weekContent.test.duration_minutes || 30
          };
          if (hasAnyContent) {
            updatePayload.mcq_questions = mcqPayload;
          }
          await axios.put(`/api/tests/${weekContent.test.test_id}`, updatePayload);
        } else if (weekId) {
          // Create new test
          await axios.post('/api/tests/create-with-structure', {
            course_id: currentCourseId,
            week_id: weekId,
            week_number: selectedWeek,
            title: weekContent.test.title,
            type: 'mcq',
            duration_minutes: weekContent.test.duration_minutes || 30,
            mcq_questions: mcqPayload
          });
        }
      }

      // Persist to content map
      setWeekContentMap(prev => ({ ...prev, [selectedWeek]: { ...weekContent } }));

      setToast({ type: 'success', message: `Content for Week ${selectedWeek} saved!` });

      // Move to next week or finish
      if (finishAfterSave) {
        setToast({ type: 'success', message: editMode ? 'Course updated successfully!' : 'Course creation completed!' });
        setTimeout(() => {
          onCourseCreated?.(currentCourseId);
          onClose();
        }, 1200);
      } else if (selectedWeek < parseInt(courseData.duration_weeks)) {
        selectWeekForContent(selectedWeek + 1);
      } else {
        setToast({ type: 'success', message: editMode ? 'Course updated successfully!' : 'Course creation completed!' });
        setTimeout(() => {
          onCourseCreated?.(currentCourseId);
          onClose();
        }, 1200);
      }
    } catch (error) {
      console.error('Step 4 error:', error);
      setToast({ type: 'error', message: error.response?.data?.message || error.response?.data?.error || 'Failed to save content' });
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="course-wizard-overlay">
      <div className="course-wizard-modal">
        {/* Stepper */}
        <div className="wizard-stepper">
          {[1, 2, 3, 4].map(s => (
            <div key={s} className={`step ${s <= step ? 'active' : ''} ${s === step ? 'current' : ''}`}>
              <div className="step-circle">{s}</div>
              <div className="step-label">
                {s === 1 && 'Course Details'}
                {s === 2 && 'Syllabus'}
                {s === 3 && 'Weekly Plan'}
                {s === 4 && 'Content'}
              </div>
            </div>
          ))}
        </div>

        {/* Step 1: Course Details */}
        {step === 1 && (
          <div className="wizard-step">
            <h3>{editMode ? 'Edit Course Details' : 'Create Course'}</h3>
            <p className="step-description">Fill in the basic information for your course. Fields marked with * are required.</p>
            <form>
              <div className="form-row">
                <div className="form-group">
                  <label>Course Title *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={courseData.course_title}
                    onChange={(e) => setCourseData({ ...courseData, course_title: e.target.value })}
                    placeholder="e.g., Introduction to Python"
                  />
                </div>

                <div className="form-group">
                  <label>Course Code *</label>
                  <input
                    type="text"
                    className={`form-control ${codeError ? 'input-error' : courseData.course_code.trim() && !codeError && !codeChecking ? 'input-success' : ''}`}
                    value={courseData.course_code}
                    onChange={(e) => setCourseData({ ...courseData, course_code: e.target.value.toUpperCase() })}
                    placeholder="e.g., CS101"
                  />
                  {codeChecking && (
                    <span className="code-hint code-hint-checking">Checking availability...</span>
                  )}
                  {codeError && (
                    <span className="code-hint code-hint-error">⚠️ {codeError}</span>
                  )}
                  {courseData.course_code.trim() && !codeError && !codeChecking && (
                    <span className="code-hint code-hint-ok">✅ Code is available</span>
                  )}
                  <button
                    type="button"
                    className="code-toggle-btn"
                    onClick={() => setShowExistingCodes(!showExistingCodes)}
                  >
                    {showExistingCodes ? 'Hide' : 'View'} existing course codes ({existingCodes.length})
                  </button>
                  {showExistingCodes && existingCodes.length > 0 && (
                    <div className="existing-codes-panel">
                      <div className="existing-codes-title">Existing Codes:</div>
                      <div className="existing-codes-list">
                        {existingCodes.map((code, i) => (
                          <span key={i} className="existing-code-tag">{code}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={courseData.description}
                  onChange={(e) => setCourseData({ ...courseData, description: e.target.value })}
                  placeholder="Course overview and description"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Program *</label>
                  <select
                    className="form-control"
                    value={courseData.program_id}
                    onChange={(e) => setCourseData({ ...courseData, program_id: e.target.value })}
                  >
                    <option value="">Select Program</option>
                    {programs.map(p => (
                      <option key={p.pgm_id} value={p.pgm_id}>{p.pgm_name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Academic Year *</label>
                  <select
                    className="form-control"
                    value={courseData.acd_id}
                    onChange={(e) => setCourseData({ ...courseData, acd_id: e.target.value })}
                  >
                    <option value="">Select Academic Year</option>
                    {academicYears.map(ay => (
                      <option key={ay.acd_id} value={ay.acd_id}>
                        {ay.year_name} ({ay.status})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Duration (weeks) *</label>
                  <input
                    type="number"
                    className="form-control"
                    min="1"
                    value={courseData.duration_weeks}
                    onChange={(e) => setCourseData({ ...courseData, duration_weeks: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select
                    className="form-control"
                    value={courseData.publish_status}
                    onChange={(e) => setCourseData({ ...courseData, publish_status: e.target.value })}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Course Thumbnail</label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={(e) => setCourseData({ ...courseData, thumbnail: e.target.files[0] })}
                />
              </div>
            </form>
          </div>
        )}

        {/* Step 2: Syllabus Units */}
        {step === 2 && (
          <div className="wizard-step">
            <h3>{editMode ? 'Edit Syllabus' : 'Create Syllabus'}</h3>
            <p className="step-description">Add the units / modules for your course. Each unit has a title and an optional description.</p>

            {units.length === 0 && (
              <div className="empty-units">
                <svg width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="#94a3b8" strokeWidth="1.2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                </svg>
                <p>No units added yet. Click the button below to add your first unit.</p>
              </div>
            )}

            <div className="unit-cards-list">
              {units.map((unit, idx) => (
                <div className="unit-card-wizard" key={unit.unit_id || `unit_${idx}`}>
                  <div className="unit-card-top">
                    <span className="unit-badge">Unit {idx + 1}</span>
                    {unit.title && unit.title.trim() && <span className="unit-check">✓</span>}
                    <button type="button" className="btn-remove-unit" onClick={() => removeUnit(idx)}>✕ Remove</button>
                  </div>
                  <div className="unit-card-body">
                    <div className="form-group">
                      <label>Unit Title *</label>
                      <input
                        type="text"
                        className="form-control unit-title-input"
                        value={unit.title}
                        onChange={(e) => updateUnit(idx, 'title', e.target.value)}
                        placeholder={`Enter title for Unit ${idx + 1}`}
                      />
                    </div>
                    <div className="form-group">
                      <label>Description (optional)</label>
                      <textarea
                        className="form-control unit-desc-input"
                        rows={4}
                        value={unit.description}
                        onChange={(e) => updateUnit(idx, 'description', e.target.value)}
                        placeholder="Topics covered, learning objectives..."
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button type="button" className="btn-add-unit" onClick={addUnit}>
              + Add Unit
            </button>

            {units.length > 0 && (
              <div className="units-status">
                {units.every(u => u.title && u.title.trim())
                  ? <span className="status-ok">✓ All {units.length} unit{units.length !== 1 ? 's' : ''} have titles — click Next to save &amp; continue</span>
                  : <span className="status-warn">⚠ {units.filter(u => !u.title || !u.title.trim()).length} unit{units.filter(u => !u.title || !u.title.trim()).length !== 1 ? 's' : ''} still need a title</span>
                }
              </div>
            )}
          </div>
        )}

        {/* Step 3: Map Units to Weeks */}
        {step === 3 && (
          <div className="wizard-step">
            <h3>Map Units to Weeks</h3>
            <p className="step-description">Assign the syllabus units you created to specific weeks. Each week can have multiple units.</p>

            <div className="week-unit-map-list">
              {weeks.map((week, wIdx) => (
                <div key={wIdx} className="week-map-card">
                  <div className="week-map-header">
                    <span className="week-map-badge">Week {week.week_number}</span>
                    <input
                      type="text"
                      className="form-control week-title-inline"
                      value={week.title}
                      onChange={(e) => {
                        const updated = [...weeks];
                        updated[wIdx].title = e.target.value;
                        setWeeks(updated);
                      }}
                      placeholder={`Week ${week.week_number} title`}
                    />
                  </div>

                  <div className="week-map-body">
                    <label className="map-label">Assign Units:</label>
                    {units.length > 0 ? (
                      <div className="unit-chip-list">
                        {units.map((unit) => {
                          const unitKey = unit.unit_id || `temp_${unit.unit_order}`;
                          const isSelected = (week.unit_ids || []).includes(unitKey);
                          return (
                            <button
                              key={unitKey}
                              type="button"
                              className={`unit-chip ${isSelected ? 'selected' : ''}`}
                              onClick={() => toggleUnitForWeek(wIdx, unitKey)}
                            >
                              <span className="chip-num">U{unit.unit_order}</span>
                              {unit.title || `Unit ${unit.unit_order}`}
                              {isSelected && <span className="chip-check">✓</span>}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="no-units-msg">No units — go back to Step 2</p>
                    )}

                    <textarea
                      className="form-control week-desc-input"
                      rows={2}
                      placeholder="Week description / notes (optional)"
                      value={week.description}
                      onChange={(e) => {
                        const updated = [...weeks];
                        updated[wIdx].description = e.target.value;
                        setWeeks(updated);
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Weekly Content */}
        {step === 4 && (
          <div className="wizard-step">
            <h3>{editMode ? 'Edit Weekly Content' : 'Add Weekly Content'}</h3>

            <div className="week-selector">
              {weeks.slice(0, courseData.duration_weeks).map(week => {
                const wMap = weekContentMap[week.week_number];
                const hasContent = wMap && (
                  wMap.materials.some(m => m.title) || wMap.assignment?.title || wMap.test?.title
                );
                return (
                  <button
                    key={week.week_number}
                    className={`week-btn ${selectedWeek === week.week_number ? 'active' : ''} ${hasContent ? 'has-content' : ''}`}
                    onClick={() => selectWeekForContent(week.week_number)}
                  >
                    Week {week.week_number}
                    {hasContent && <span className="week-dot">●</span>}
                  </button>
                );
              })}
            </div>

            <p className="week-editing-label">Editing: <strong>{weeks.find(w => w.week_number === selectedWeek)?.title || `Week ${selectedWeek}`}</strong></p>

            {/* Materials */}
            <div className="content-section">
              <h5>📚 Course Materials</h5>
              {weekContent.materials.map((material, idx) => (
                <div key={idx} className={`material-input ${material.content_id ? 'existing-item' : ''}`}>
                  {material.content_id && <span className="saved-badge">✓ Saved</span>}
                  <select
                    className="form-control"
                    value={material.type}
                    onChange={(e) => {
                      const updated = [...weekContent.materials];
                      updated[idx].type = e.target.value;
                      setWeekContent({ ...weekContent, materials: updated });
                    }}
                  >
                    <option value="video">Video</option>
                    <option value="note">Note</option>
                    <option value="others">Others</option>
                  </select>

                  <input
                    type="text"
                    className="form-control"
                    placeholder="Title"
                    value={material.title}
                    onChange={(e) => {
                      const updated = [...weekContent.materials];
                      updated[idx].title = e.target.value;
                      setWeekContent({ ...weekContent, materials: updated });
                    }}
                  />

                  {material.url && !material.file && (
                    <div className="existing-file-info">
                      📎 Existing file: {material.url.split('/').pop()}
                    </div>
                  )}
                  <input
                    type="file"
                    className="form-control"
                    accept="*/*"
                    onChange={(e) => {
                      const updated = [...weekContent.materials];
                      const file = e.target.files[0];
                      if (file) {
                        updated[idx].file = file;
                        if (!updated[idx].title) {
                          updated[idx].title = file.name.replace(/\.[^/.]+$/, '');
                        }
                      }
                      setWeekContent({ ...weekContent, materials: updated });
                    }}
                  />
                  {material.file && (
                    <div className="selected-file-info">
                      📄 Selected: {material.file.name}
                    </div>
                  )}

                  <textarea
                    className="form-control"
                    placeholder="Description"
                    rows={2}
                    value={material.description}
                    onChange={(e) => {
                      const updated = [...weekContent.materials];
                      updated[idx].description = e.target.value;
                      setWeekContent({ ...weekContent, materials: updated });
                    }}
                  />

                  <button
                    type="button"
                    className="btn-remove"
                    onClick={() => {
                      const updated = weekContent.materials.filter((_, i) => i !== idx);
                      setWeekContent({ ...weekContent, materials: updated.length > 0 ? updated : [{ type: 'video', title: '', url: '', file: null, description: '' }] });
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => setWeekContent({
                  ...weekContent,
                  materials: [...weekContent.materials, { type: 'video', title: '', url: '', file: null, description: '' }]
                })}
              >
                + Add Material
              </button>
            </div>

            {/* Assignment */}
            <div className="content-section">
              <h5>📝 Assignment (Optional) {weekContent.assignment?.assignment_id && <span className="saved-badge">✓ Saved</span>}</h5>
              <input
                type="text"
                className="form-control"
                placeholder="Assignment title"
                value={weekContent.assignment?.title || ''}
                onChange={(e) => setWeekContent({
                  ...weekContent,
                  assignment: { ...weekContent.assignment, title: e.target.value }
                })}
              />
              <textarea
                className="form-control"
                rows={3}
                placeholder="Assignment instructions"
                value={weekContent.assignment?.description || ''}
                onChange={(e) => setWeekContent({
                  ...weekContent,
                  assignment: { ...weekContent.assignment, description: e.target.value }
                })}
              />
              <input
                type="date"
                className="form-control"
                value={weekContent.assignment?.due_date || ''}
                onChange={(e) => setWeekContent({
                  ...weekContent,
                  assignment: { ...weekContent.assignment, due_date: e.target.value }
                })}
              />
            </div>

            {/* Test */}
            <div className="content-section">
              <h5>🧪 Weekly Test (Optional) {weekContent.test?.test_id && <span className="saved-badge">✓ Saved</span>}</h5>
              <div className="form-row">
                <div className="form-group">
                  <label>Test Title</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Test title"
                    value={weekContent.test?.title || ''}
                    onChange={(e) => setWeekContent({
                      ...weekContent,
                      test: { ...weekContent.test, title: e.target.value }
                    })}
                  />
                </div>
                <div className="form-group">
                  <label>Duration (minutes)</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Duration (minutes)"
                    value={weekContent.test?.duration_minutes || 30}
                    onChange={(e) => setWeekContent({
                      ...weekContent,
                      test: { ...weekContent.test, duration_minutes: e.target.value }
                    })}
                  />
                </div>
              </div>

              {/* Questions Editor */}
              {weekContent.test?.title && (
                <div className="mcq-editor">
                  <div className="mcq-editor-header">
                    <h5 className="mcq-editor-title">
                      📋 MCQ Questions ({(weekContent.test?.questions || []).length})
                    </h5>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => {
                        const questions = [...(weekContent.test?.questions || [])];
                        questions.push({
                          question_text: '', option_a: '', option_b: '', option_c: '', option_d: '',
                          correct_option: 'a', marks: 1
                        });
                        setWeekContent({ ...weekContent, test: { ...weekContent.test, questions } });
                      }}
                    >
                      + Add Question
                    </button>
                  </div>

                  {(weekContent.test?.questions || []).length === 0 && (
                    <div className="mcq-empty">
                      No questions yet. Click "+ Add Question" to add MCQ questions.
                    </div>
                  )}

                  {(weekContent.test?.questions || []).map((q, qIdx) => (
                    <div key={qIdx} className="mcq-question-card">
                      <div className="mcq-question-header">
                        <span className="mcq-question-num">Question {qIdx + 1}</span>
                        <button
                          type="button"
                          className="btn-remove-unit"
                          onClick={() => {
                            const questions = weekContent.test.questions.filter((_, i) => i !== qIdx);
                            setWeekContent({ ...weekContent, test: { ...weekContent.test, questions } });
                          }}
                        >
                          ✕ Remove
                        </button>
                      </div>

                      <div className="form-group">
                        <label>Question Text *</label>
                        <textarea
                          className="form-control"
                          rows={2}
                          placeholder="Enter the question..."
                          value={q.question_text}
                          onChange={(e) => {
                            const questions = [...weekContent.test.questions];
                            questions[qIdx] = { ...questions[qIdx], question_text: e.target.value };
                            setWeekContent({ ...weekContent, test: { ...weekContent.test, questions } });
                          }}
                        />
                      </div>

                      <div className="mcq-options-grid">
                        {['a', 'b', 'c', 'd'].map(opt => (
                          <div key={opt} className="mcq-option-item">
                            <label className={`mcq-option-label ${q.correct_option === opt ? 'correct' : ''}`}>
                              Option {opt.toUpperCase()}
                              {q.correct_option === opt && <span className="mcq-correct-tag">✓ Correct</span>}
                            </label>
                            <input
                              type="text"
                              placeholder={`Option ${opt.toUpperCase()}`}
                              value={q[`option_${opt}`] || ''}
                              onChange={(e) => {
                                const questions = [...weekContent.test.questions];
                                questions[qIdx] = { ...questions[qIdx], [`option_${opt}`]: e.target.value };
                                setWeekContent({ ...weekContent, test: { ...weekContent.test, questions } });
                              }}
                              className={`form-control ${q.correct_option === opt ? 'input-success' : ''}`}
                            />
                          </div>
                        ))}
                      </div>

                      <div className="mcq-footer-row">
                        <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
                          <label>Correct Answer</label>
                          <select
                            className="form-control"
                            value={q.correct_option}
                            onChange={(e) => {
                              const questions = [...weekContent.test.questions];
                              questions[qIdx] = { ...questions[qIdx], correct_option: e.target.value };
                              setWeekContent({ ...weekContent, test: { ...weekContent.test, questions } });
                            }}
                          >
                            <option value="a">Option A</option>
                            <option value="b">Option B</option>
                            <option value="c">Option C</option>
                            <option value="d">Option D</option>
                          </select>
                        </div>
                        <div className="form-group" style={{ marginBottom: 0, width: '100px' }}>
                          <label>Marks</label>
                          <input
                            type="number"
                            className="form-control"
                            min="1"
                            value={q.marks || 1}
                            onChange={(e) => {
                              const questions = [...weekContent.test.questions];
                              questions[qIdx] = { ...questions[qIdx], marks: parseInt(e.target.value) || 1 };
                              setWeekContent({ ...weekContent, test: { ...weekContent.test, questions } });
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="wizard-navigation">
          <button
            className="btn-secondary"
            onClick={() => {
              if (step === 4) {
                // Save current week to map before going back
                setWeekContentMap(prev => ({ ...prev, [selectedWeek]: { ...weekContent } }));
              }
              step > 1 ? setStep(step - 1) : onClose();
            }}
          >
            {step === 1 ? 'Cancel' : 'Back'}
          </button>

          <div className="progress-info">
            Step {step} of 4
          </div>

          <div className="nav-buttons-right">
            {step === 4 && (
              <button
                className="btn-secondary"
                style={{ background: '#059669', color: 'white', border: 'none' }}
                onClick={handleFinishCourse}
              >
                ✓ Finish Course
              </button>
            )}
            <button
              className="btn-primary"
              onClick={() => {
                if (step === 1) handleStep1Submit();
                else if (step === 2) handleStep2Submit();
                else if (step === 3) handleStep3Submit();
                else if (step === 4) handleStep4Submit();
              }}
            >
              {step === 4 ? (selectedWeek < parseInt(courseData.duration_weeks) ? 'Save & Next Week' : 'Save & Finish') : 'Next'}
            </button>
          </div>
        </div>

        {toast && <Toast text={toast.message} type={toast.type} />}
      </div>
    </div>
  );
};

export default CourseWizard;
